import React from 'react';
import { ArrowLeft, Shield, Lock, Mail, Globe, Server, MessageSquare, ArrowRight } from 'lucide-react';

const SecureCommunicationsPage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="secure-comms-page">
      <header className="bg-black py-4 px-4 md:px-8">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 text-white/70 hover:text-white text-sm transition-colors"><ArrowLeft className="w-4 h-4" /><span className="tracking-wide">Back to Home</span></a>
          <img src="/logos/sag-logo.png" alt="SAG" className="h-8 w-auto" />
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 md:py-24 px-4 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <span className="text-[#C9A227] text-xs tracking-[0.3em] uppercase block mb-4">Powered by Sekur</span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-light text-black mb-4">Secure Communications</h1>
          <p className="text-gray-500 text-base md:text-lg max-w-2xl mx-auto mb-6">
            100% Secure and Private Platform. No Data Mining. No Big Tech Hosting.
            Swiss-hosted, military-grade encrypted messaging and email.
          </p>
          <div className="w-16 h-[2px] bg-[#C9A227] mx-auto" />
        </div>
      </section>

      {/* Features */}
      <section className="py-16 px-4 bg-gray-50 border-t border-gray-200">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              { icon: Lock, title: 'Proprietary High Encryption', desc: 'All communications encrypted end-to-end — messages, group chats, voice recordings, files, and emails. Self-destruct chats ensure nothing remains on your device.' },
              { icon: Shield, title: 'Swiss Data Privacy Protection', desc: 'All user data protected by the Swiss Federal Data Protection Act (FADP) — some of the strongest privacy protection in the world for individuals and entities.' },
              { icon: MessageSquare, title: 'Secure Messaging', desc: 'Send highly secure and private chat messages. Content is not monitored and messages are not stored on servers. Zero discovery privacy guaranteed.' },
              { icon: Mail, title: 'Secure Email', desc: 'Email anyone on the Sekur network with full privacy. Conversations are encrypted and remain private. No part of your conversation is stored.' },
              { icon: Server, title: 'Swiss-Only Hosting', desc: 'All servers are in Switzerland. No AWS, no Google Cloud, no Microsoft Azure. Data processed only in Swiss bank-approved, ISO-certified data centers.' },
              { icon: Globe, title: 'No Big Tech Dependencies', desc: 'Proprietary technology and platform — no open source code. No data mining, no tracking, no third-party access to your communications.' },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="p-6 bg-white border border-gray-200">
                  <Icon className="w-6 h-6 text-[#C9A227] mb-4" />
                  <h3 className="text-sm font-medium text-black uppercase tracking-wider mb-2">{item.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 md:py-24 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-[#C9A227] text-xs tracking-[0.3em] uppercase block mb-4">Plans</span>
            <h2 className="text-2xl sm:text-3xl font-light text-black mb-4">Pricing</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { name: 'Solo', price: '$9', period: '/month', annual: '$90/year', desc: 'Ideal for Chat & Email', users: '1 User', popular: false },
              { name: 'Team', price: '$45', period: '/month', annual: '$450/year', desc: 'Professionals & Families', users: 'Multi User', popular: true },
              { name: 'Team Pro', price: '$90', period: '/month', annual: '$900/year', desc: 'Advanced Teams', users: 'Multi User', popular: false },
              { name: 'SME', price: '$225', period: '/month', annual: '$2,250/year', desc: 'Small & Medium Enterprise', users: 'Multi User', popular: false },
            ].map((plan, i) => (
              <div key={i} className={`p-6 border ${plan.popular ? 'border-[#C9A227] bg-[#C9A227]/5' : 'border-gray-200'} relative`}>
                {plan.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#C9A227] text-black text-[10px] tracking-wider uppercase font-medium">Most Popular</span>}
                <h3 className="text-sm font-medium text-black uppercase tracking-wider mb-1">{plan.name}</h3>
                <p className="text-gray-400 text-xs mb-4">{plan.desc}</p>
                <div className="mb-4">
                  <span className="text-3xl font-light text-black">{plan.price}</span>
                  <span className="text-gray-400 text-sm">{plan.period}</span>
                  <p className="text-gray-400 text-xs mt-1">{plan.annual}</p>
                </div>
                <ul className="space-y-2 mb-6 text-xs text-gray-500">
                  {['Military Grade Encryption', 'Swiss Privacy', 'Self-Destruct Messages', 'Unlimited Messaging', plan.users].map((f, j) => (
                    <li key={j} className="flex items-center gap-2"><span className="w-1 h-1 bg-[#C9A227] rounded-full" />{f}</li>
                  ))}
                </ul>
                <a href="https://sekursuite.com/en/sag" target="_blank" rel="noopener noreferrer" className="block w-full py-3 bg-[#1e3a5f] text-white text-xs tracking-wider uppercase text-center font-medium hover:bg-black transition-colors">
                  Get Started
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-[#1e3a5f]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-light text-white mb-4">Privacy Has Arrived</h2>
          <p className="text-white/50 text-sm mb-8">Join Sekur and communicate privately and securely with your circle. Encrypted, Swiss-hosted, zero discovery.</p>
          <a href="https://sekursuite.com/en/sag" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-4 bg-[#C9A227] text-black text-xs tracking-[0.15em] uppercase font-medium hover:bg-white transition-colors">
            Sign Up Now <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      <footer className="bg-black py-6 px-4 text-center">
        <p className="text-white/40 text-xs tracking-wider">Sebastian Alliance Group &bull; Secure Communications powered by Sekur</p>
      </footer>
    </div>
  );
};

export default SecureCommunicationsPage;
